import { differenceInSeconds } from 'date-fns';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { component, ComponentJson, ComponentTuple, ObjectManager, property } from '../../../concepts/objects';
import { DateType } from '../../../concepts/objects/DateType';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { ColorGroups, Colors } from '../../../ui';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { Figure, Physical, PotentialInteraction, RelativeTo, TransformHints, TransformScope } from '../../canvas/entities/Physical';
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical';
import { Snappable } from '../../canvas/entities/Snappable';
import { Textable } from '../../canvas/entities/Textable';
import { PreferredTextColor } from '../../canvas/entities/TextConventions';
import { UIField } from '../../canvas/entities/UIField';
import { GraffitiTarget } from '../../canvas/visualization/Graffiti';
import { Commentable } from '../annotations/Commentable';
import { commentsOpen } from '../annotations/CommentCommands';
import { Tag } from '../annotations/Tag';
import { tagsToggleEdit } from '../annotations/TaggingCommands';
import { TagSupport } from '../annotations/TagSupport';
import { Audited } from '../auditing/Audited';
import { AutoLayoutSubject } from '../autoLayout/AutoLayout';
import { ConnectTarget } from '../connector/ConnectTarget';
import { TimelineFrameAddon } from '../frame/addons/TimelineFrameAddon';
import { Frame } from '../frame/Frame';
import { Pingable } from '../presentation/Pingable';
import { RoadmapCardEndDate, RoadmapCardStartDate, RoadmapCardTargetDate } from './RoadmapCardAssocs';
import { RoadmapCardLabelField } from './RoadmapCardLabelField';
import { RoadmapCardLayout } from './RoadmapCardLayout';
import { roadmapCardPropertyLayout } from './RoadmapCardPropertyLayouts';

const mutation = defineCommand(
    'roadmapCardMutate',
    'RoadmapCard Mutate',
    (r, [id]: [string]) => {
        const tc = r.call(ObjectManager).get(id, RoadmapCard);
        const isNew = differenceInSeconds(new Date(), tc.created) < 1;
        // If the card was created less than a second ago it's new.  If so, default to the label field.  This is basically a hack to prevent
        // the "plop" from adding a new index card selecting the body instead of the label.
        r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field', isNew ? 'label' : ''));
    },
    [],
);

const invocation = defineCommand(
    'roadmapCardInvoke',
    null,
    (r, [id, hint]: [string, string?]) => {
        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);
        const card = objects.get(id, RoadmapCard);
        if (hint === 'externalLink') {
            window.open(card.externalLink, '_blank');
        }
        else if (hint === 'comments') {
            dispatch(commentsOpen, [id]);
        }
        else {
            const tag = objects.getMaybe(hint, Tag);
            if (!!tag) dispatch(tagsToggleEdit, [tag.id, true]);
        }
    },
    [],
);

const roadmapCardDef = {
    name: 'RoadmapCard',
    implements: [
        Audited,
        AutoLayoutSubject,
        Commentable,
        ConnectTarget,
        GraffitiTarget,
        Physical,
        Pingable,
        PreferredTextColor,
        Snappable,
        TagSupport,
        Textable
    ],
    mutation,
    invocation,
    propertyLayout: roadmapCardPropertyLayout,
};

@component(roadmapCardDef)
export class RoadmapCard extends RectangularPhysical implements Audited, Pingable, PreferredTextColor, Snappable, TagSupport, Textable {

    @property({ type: 'string', default: ColorGroups[3][0], style: true })
    public background!: string;

    @property({ type: 'string', oldName: 'title|value' })
    public label!: string;

    @property({ type: 'string', default: 'solid' })
    public style!: string;

    @property({ type: 'string' })
    public externalLink!: string;

    @property({ type: 'string' })
    public userId!: string;

    @property({ type: DateType })
    public created!: Date;

    @property({ type: 'string', default: 'ontime' })
    public punctuality: 'early' | 'ontime' | 'late';

    @property({ type: 'number', default: -1 })
    public marker: number;

    public get minWidth(): number {
        return 100;
    }

    public get minHeight(): number {
        return 40;
    }

    public get shouldSnap() {
        return true;
    }

    // @memoUsing(
    //     'width', 'height', 'marker', 'punctuality', 'externalLink',
    //     CommentCountAttr, CommentOpenAttr, TaskRecordAttr, VoteCountAttr,
    // )

    @memoUsing('ticker', 'children')
    public get layout(): RoadmapCardLayout {
        return RoadmapCardLayout(this);
    }

    @memoUsing('layout')
    public get uiFields(): readonly UIField[] {
        return [RoadmapCardLabelField(this)];
    }

    public timeline(): TimelineFrameAddon {
        const frame = this.ancestors().find(x => x.is(Frame)) as Frame;
        return frame?.addon(TimelineFrameAddon);
    }

    public queryTransformHints(singular: boolean): TransformHints {
        if (singular) {
            return TransformHints({
                visible: true,
                translate: true,
                scaleX: true,
                scaleY: true,
                tweaks: !!this.timeline()
                    ? [{ offset: 4, style: 'blob' as const }]
                    : [],
            });
        }
        return TransformHints({
            scaleX: true,
            scaleY: true,
            translate: true,
            visible: true,
        });
    }

    public createFigure(): Figure {
        const mt = this.transform(RelativeTo.Model);

        const e = mt.apply(new Point(
            this.marker >= 0 ? this.marker : this.width,
            this.height / 2
        ));

        // If you are WTFing at this, read the comments in applyTransformation.
        const o =
            (this.punctuality === 'early' ? 2 : null) ??
            (this.punctuality === 'late' ? 1 : null) ??
            0;

        const f = e.add([o, 0]);

        return super.createFigure().concat([e, f]);
    }

    public applyTransformation(scope: TransformScope, from: Figure, to: Figure): this {
        // Figures are always model level, so we need to apply the inverse transform of the parent to localize it.
        const tlt = this.modelLocalTransform();
        const tmt = this.localModelTransform();

        const [fa, fb, _fc, _fd, fe, ff] = tlt.apply(from);
        const [ta, tb, _tc, _td, te, tf] = tlt.apply(to);

        // Project the e figure value onto the axis of a->b, as the user could drag e to anywhere.
        const pfe = fe.subtract(fa).project(Point.vector(fa, fb)).add(fa);
        const pte = te.subtract(ta).project(Point.vector(ta, tb)).add(ta);

        // Note: the mess below is because we can't encode non-vector values in the state machine to/from data.  If we could this
        // would all be way easier / sane.

        // So this is very messy, but basically because we want to have an organically feeling tweak for the marker, where if we move
        // across the edge we flip the marker punctuality and flip back again if we move back inside the same transformation, we need
        // to encode the marker punctuality  value in the "from" data, otherwise we can't tell if we're moving from early to late or
        // late to early.  So we do this using an f point within the figure.
        const fromPunc =
            (Math.round(Point.vector(fe, ff).length()) === 1 ? 'late' : null) ?? // |fe->ff| === 1 then "late"
            (Math.round(Point.vector(fe, ff).length()) === 2 ? 'early' : null) ?? // |fe->ff| === 2 then "early"
            'ontime';

        if (scope.type === 'tweak') {

            // For a tweak, we need to know if the e point has crossed the end of a->b, so we know what to do with the new punctuality
            // value.  We do this by comparing the length of the vector from a->e to the length of the vector from a->b.  If the length
            // of a->e is greater than a->b then we know we've crossed the end of the line, or we've gone really far in the opposite
            // direction.  Check the normalized vectors are equal to discount the latter.

            const tape = Point.vector(ta, pte);
            const tatb = Point.vector(ta, tb);
            const crossed = (tape.length() > tatb.length() && tape.normalize().round(4).equals(tatb.normalize().round(4)));

            // Set the new punctuality value
            if (pte.distanceTo(tb) < 1) {
                this.punctuality = 'ontime';
            }
            else if (fromPunc === 'ontime') {
                this.punctuality = crossed ? 'late' : 'early';
            }
            else {
                this.punctuality = crossed
                    ? (fromPunc === 'early' ? 'late' : 'early')
                    : (fromPunc === 'early' ? 'early' : 'late');
            }

            // Are we extending beyond the end?
            if (ta.distanceTo(pte) > ta.distanceTo(tb)) {
                this.marker = ta.distanceTo(tb);
            }
            else {
                this.marker = pte.distanceTo(tb) < ta.distanceTo(tb)
                    ? pte.distanceTo(ta)
                    : 0;
            }

            const w = Math.max(
                ta.distanceTo(tb),
                ta.distanceTo(pte)
            );

            this.setTransformationResult(
                this.x,
                this.y,
                this.r,
                w,
                this.height,
            );

        }
        else if (scope.type === 'scale') {
            super.applyTransformation(scope, from, to);
            if (fromPunc === 'ontime') {
                this.marker = ta.distanceTo(tb);
            }
            else {
                this.marker = Math.min(fa.distanceTo(pfe), ta.distanceTo(tb));
            }
            // If we resized to ontime - set value.
            if (pte.distanceTo(tb) < 1) {
                this.punctuality = 'ontime';
                this.marker = -1;
            }
        }
        else {
            super.applyTransformation(scope, from, to);
        }


        return this;
    }

    public area(to: RelativeTo | Physical = RelativeTo.Parent): Rect {

        const comb = Rect.fromMany([this.layout.base, this.layout.externalLink]);
        return Rect.fromPoints(this.transform(to).apply(comb));
    }

    public test(mpt: Point): boolean {
        const lpt = this.toRelative(mpt);
        return this.layout.base.contains(lpt) || this.layout.externalLink.contains(lpt);
    }

    public testInteraction({ type, mpt }: PotentialInteraction, _force?: boolean, _selected?: boolean): [boolean, string] {
        const { base, externalLink, tags } = this.layout;
        const rpt = this.toRelative(mpt);
        if (base.contains(rpt) || externalLink.contains(rpt)) {
            if (type === 'invoke') {
                if (externalLink.contains(rpt)) return [true, 'externalLink'];
                for (const [id, area] of Object.entries(tags)) {
                    if (id === 'all') continue;
                    if (area.contains(rpt)) return [true, id];
                }
            }
            else {
                return [true, 'default'];
            }
        }
        return [false, 'none'];
    }

    public getPreferredTextColor(): string {
        return Colors.contrastTextColorFor(this.background);
    }

    public toEmptyTuple(to?: Physical | RelativeTo): ComponentTuple {
        const ct = super.toShallowTuple(to);
        ct[2].label = '';
        ct[2]['title|value'] = '';
        return ct;
    }

    public toJson(): ComponentJson {
        const json = super.toJson();
        json.startDate = RoadmapCardStartDate.get(this);
        json.targetDate = RoadmapCardTargetDate.get(this);
        json.endDate = RoadmapCardEndDate.get(this);
        return json;
    }

    public toText(): string {
        return this.label;
    }

    public toHumanString(): string {
        return `Block ("${this.label.slice(0, 20)}")`;
    }
}
